/**
 * DOM overlay controller.
 *
 * Deliberately plain DOM. This layer has to be handed to WebXR as a
 * `domOverlay` root, has to survive an aggressive repaint budget while a
 * camera feed and a WebGL scene both run, and never has more than a dozen
 * live nodes. A framework would cost more than it returns here.
 */

import type { Badge } from '../content/badges';
import { CATEGORY_LABEL, type GardenFact } from '../content/facts';
import type { Species, SpeciesId } from '../content/species';
import type { Mutation, Weather } from '../content/weather';
import { formatStampDate, MAX_FROM, MAX_NAME, type VisitorStamp } from '../game/progress';
import { Journal, type JournalView } from './journal';

export type ScreenId = 'intro' | 'blocked' | 'scanning' | 'play';

/** Everything the register sheet needs, computed by `App`. */
export interface RegisterView {
  ranked: VisitorStamp[];
  activeId: string | null;
  streak: number;
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (c) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[c] ?? c,
  );
}

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`UI element #${id} is missing from index.html`);
  return node as T;
}

/** Small leaf glyph tinted per species, drawn inline so there are no requests. */
export function speciesGlyph(species: Species): string {
  const leaf = `#${species.leaf.toString(16).padStart(6, '0')}`;
  const bloom = `#${species.bloom.toString(16).padStart(6, '0')}`;
  return `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 22V11" stroke="${leaf}" stroke-width="1.8" stroke-linecap="round" fill="none"/>
      <path d="M12 11c-3.4-1.2-5.6-4-5.6-7.2C10 4.4 12 7 12 11Z" fill="${leaf}"/>
      <path d="M12 11c3.4-1.2 5.6-4 5.6-7.2C14 4.4 12 7 12 11Z" fill="${leaf}" opacity="0.75"/>
      <circle cx="12" cy="14.5" r="2.1" fill="${bloom}"/>
    </svg>`;
}

export interface UICallbacks {
  onBegin(): void;
  onPlace(): void;
  onSpeciesChange(id: SpeciesId): void;
  onToggleMute(): void;
  onMoveGarden(): void;
  onShowRegister(): void;
  onShowJournal(): void;
  onShowWorld(): void;
  onOpenPack(): void;
  onShowShare(): void;
  /** The name toggle moved; the card and the link both have to be rebuilt. */
  onShareNameChange(include: boolean): void;
  onSaveImage(): void;
  onCopyLink(): void;
  onLeaveVisit(): void;
  onSign(name: string, from: string): void;
}

/** The game data the overlay renders, handed in rather than imported. */
export interface UIOptions {
  /** The species rail, in display order. */
  species: readonly Species[];
  /** Which chip is selected when the game opens. */
  selected: SpeciesId;
}

export class UI {
  private screens: Record<ScreenId, HTMLElement>;
  private toastTimer: number | null = null;
  private hintTimer: number | null = null;
  private selected: SpeciesId;
  private species: readonly Species[];

  private cb: UICallbacks;
  private journal: Journal;

  constructor(cb: UICallbacks, options: UIOptions) {
    this.cb = cb;
    this.species = options.species;
    this.selected = options.selected;
    this.screens = {
      intro: el('screen-intro'),
      blocked: el('screen-blocked'),
      scanning: el('screen-scanning'),
      play: el('screen-play'),
    };

    el('btn-begin').addEventListener('click', () => this.cb.onBegin());
    el('btn-place').addEventListener('click', () => this.cb.onPlace());
    el('btn-mute').addEventListener('click', () => this.cb.onToggleMute());
    el('btn-move').addEventListener('click', () => this.cb.onMoveGarden());
    el('btn-fact-close').addEventListener('click', () => this.closeFact());

    el('btn-level').addEventListener('click', () => this.cb.onShowJournal());
    this.journal = new Journal(el('journal-modal'), {
      onClose: () => this.journal.close(),
      onRegister: () => {
        this.journal.close();
        this.cb.onShowRegister();
      },
    });
    el('btn-world').addEventListener('click', () => this.cb.onShowWorld());
    el('btn-pack').addEventListener('click', () => this.cb.onOpenPack());
    el('btn-share').addEventListener('click', () => this.cb.onShowShare());
    el('btn-share-close').addEventListener('click', () => this.closeShare());
    el('btn-share-image').addEventListener('click', () => this.cb.onSaveImage());
    el('btn-share-link').addEventListener('click', () => this.cb.onCopyLink());
    el('btn-visiting-leave').addEventListener('click', () => this.cb.onLeaveVisit());
    el('share-name').addEventListener('change', () =>
      this.cb.onShareNameChange((el('share-name') as HTMLInputElement).checked),
    );
    el('btn-register-close').addEventListener('click', () => this.closeRegister());
    el('btn-away-close').addEventListener('click', () => this.closeAway());
    el('btn-sign').addEventListener('click', () => this.showSign());
    el('btn-sign-cancel').addEventListener('click', () => this.closeSign());
    el('btn-sign-save').addEventListener('click', () => this.submitSign());
    el<HTMLInputElement>('sign-from').addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') this.submitSign();
    });

    this.buildSpeciesRail();
  }

  /* -------------------------------------------------------------- *
   * Screens
   * -------------------------------------------------------------- */

  show(screen: ScreenId): void {
    for (const [id, node] of Object.entries(this.screens)) {
      node.dataset.active = String(id === screen);
    }
  }

  /** Text under the Begin button describing which AR mode will run. */
  setModeNote(label: string, detail: string): void {
    el('begin-sub').textContent = label;
    el('mode-note').textContent = detail;
  }

  setBeginEnabled(enabled: boolean): void {
    (el<HTMLButtonElement>('btn-begin')).disabled = !enabled;
  }

  showBlocked(reason: string): void {
    el('blocked-reason').textContent = reason;
    this.show('blocked');
  }

  /* -------------------------------------------------------------- *
   * Scanning
   * -------------------------------------------------------------- */

  setScanText(text: string): void {
    el('scan-text').textContent = text;
  }

  setPlaceEnabled(enabled: boolean): void {
    (el<HTMLButtonElement>('btn-place')).disabled = !enabled;
  }

  /* -------------------------------------------------------------- *
   * Play HUD
   * -------------------------------------------------------------- */

  setTreeCount(count: number): void {
    const node = el('tree-count');
    node.textContent = String(count);
    el('tree-word').textContent = count === 1 ? 'tree' : 'trees';
    // Retrigger the bump animation.
    node.classList.remove('bump');
    void node.offsetWidth;
    node.classList.add('bump');
    window.setTimeout(() => node.classList.remove('bump'), 220);
  }

  /**
   * The seedling balance, and whether a pack can be bought right now.
   *
   * Both the counter and the pack button stay hidden until the player has
   * actually earned something. Somebody who opens the link once and
   * plants one tree should never be shown a currency they have no use for —
   * the economy is meant to appear as a consequence of harvesting, not as
   * furniture that was always there.
   */
  setSeedlings(
    balance: number,
    cost: number,
    size: number,
    blocked: string | null,
    /**
     * Whether the player has ever harvested. Not `balance > 0` — spending the
     * last seedling would otherwise make the whole economy vanish from the
     * HUD, which reads as a bug rather than as a balance of zero.
     */
    seen: boolean,
  ): void {
    el('seedlings').dataset.show = seen ? 'true' : 'false';
    el('seedlings-num').textContent = String(balance);

    const pack = el('btn-pack') as HTMLButtonElement;
    pack.dataset.show = seen ? 'true' : 'false';
    pack.disabled = blocked !== null;
    el('pack-title').textContent = blocked ?? 'Open a seed pack';
    el('pack-sub').textContent =
      blocked === null ? `${size} seeds · ${cost} seedlings` : `${balance} banked`;
  }

  setProgress(planted: number, capacity: number, label: string): void {
    const pct = capacity > 0 ? Math.min(100, (planted / capacity) * 100) : 0;
    (el('progress-fill')).style.width = `${pct}%`;
    el('progress-label').textContent = label;
  }

  /** Transient centre hint. Auto-hides. */
  showHint(text: string, ms = 3200): void {
    const node = el('tap-hint');
    node.textContent = text;
    node.dataset.show = 'true';
    if (this.hintTimer !== null) window.clearTimeout(this.hintTimer);
    this.hintTimer = window.setTimeout(() => {
      node.dataset.show = 'false';
    }, ms);
  }

  hideHint(): void {
    el('tap-hint').dataset.show = 'false';
  }

  setMuted(muted: boolean): void {
    const btn = el('btn-mute');
    btn.dataset.muted = String(muted);
    btn.setAttribute('aria-label', muted ? 'Unmute sound' : 'Mute sound');
  }

  /* -------------------------------------------------------------- *
   * Species rail
   * -------------------------------------------------------------- */

  private buildSpeciesRail(): void {
    const rail = el('species-rail');
    rail.innerHTML = '';

    for (const species of this.species) {
      const id = species.id;
      const chip = document.createElement('button');
      chip.className = 'species-chip';
      chip.type = 'button';
      chip.setAttribute('role', 'radio');
      chip.setAttribute('aria-checked', String(id === this.selected));
      chip.dataset.species = id;
      chip.innerHTML = `
        <span class="chip-swatch">${speciesGlyph(species)}</span>
        <span class="chip-name">${species.name}</span>
        <span class="chip-ar">${species.nameAr}</span>`;

      chip.addEventListener('click', () => this.selectSpecies(id));
      rail.appendChild(chip);
    }
  }

  selectSpecies(id: SpeciesId): void {
    this.selected = id;
    for (const chip of document.querySelectorAll<HTMLElement>('.species-chip')) {
      chip.setAttribute('aria-checked', String(chip.dataset.species === id));
    }
    this.cb.onSpeciesChange(id);
  }

  get selectedSpecies(): Species {
    // The selection only ever comes from a chip built from this list.
    return this.species.find((s) => s.id === this.selected) ?? this.species[0];
  }

  /* -------------------------------------------------------------- *
   * Fact card and toast
   * -------------------------------------------------------------- */

  private factQueue: GardenFact[] = [];
  private factOpen = false;

  showFact(fact: GardenFact, unlocked = 0, total = 0): void {
    if (this.factOpen) {
      this.factQueue.push(fact);
      return;
    }
    this.factOpen = true;
    const image = el<HTMLImageElement>('fact-image');
    if (fact.image) {
      // Set `src` before showing. Swapping it on a visible element leaves the
      // previous card's picture up for a frame, which reads as the wrong
      // illustration rather than as a load.
      image.src = fact.image;
      image.dataset.show = 'true';
    } else {
      image.dataset.show = 'false';
      image.removeAttribute('src');
    }
    el('fact-kicker').textContent = CATEGORY_LABEL[fact.category];
    el('fact-headline').textContent = fact.headline;
    el('fact-body').textContent = fact.body;
    el('fact-progress').textContent =
      total > 0 ? `${unlocked} of ${total} facts uncovered` : '';
    const modal = el('fact-modal');
    modal.dataset.open = 'true';
    modal.setAttribute('aria-hidden', 'false');
  }

  private closeFact(): void {
    const modal = el('fact-modal');
    modal.dataset.open = 'false';
    modal.setAttribute('aria-hidden', 'true');
    this.factOpen = false;

    const next = this.factQueue.shift();
    if (next) window.setTimeout(() => this.showFact(next), 240);
  }

  /**
   * True while anything modal is up.
   *
   * `App` consults this before routing a tap into the scene: without it, a
   * tap that dismisses a card would fall straight through to the soil and sow
   * a seed the player never asked for.
   */
  get isFactOpen(): boolean {
    return (
      this.factOpen ||
      isOpen('register-modal') ||
      isOpen('share-modal') ||
      isOpen('sign-modal') ||
      isOpen('away-modal') ||
      this.journal.isOpen
    );
  }

  /* ---------------------------------------------------------------- *
   * Share
   * ---------------------------------------------------------------- */

  showShare(signedName: string | null): void {
    const row = el('share-name-row');
    const box = el('share-name') as HTMLInputElement;
    // Nothing to offer if nobody has signed the register — and offering an
    // empty checkbox would imply there is a name in the link when there is not.
    row.hidden = !signedName;
    if (!signedName) box.checked = false;
    el('share-name-sub').textContent = signedName
      ? `“${signedName}” travels with the link and the image`
      : '';
    setOpen('share-modal', true);
  }

  /** Whether the sharer has opted their name in. Always false when unsigned. */
  get shareIncludesName(): boolean {
    return (el('share-name') as HTMLInputElement).checked;
  }

  setSharePreview(dataUrl: string): void {
    (el('share-preview') as HTMLImageElement).src = dataUrl;
  }

  setShareNote(note: string): void {
    el('share-note').textContent = note;
  }

  private closeShare(): void {
    setOpen('share-modal', false);
  }

  /** The banner shown while looking at somebody else's garden. */
  setVisiting(who: string | null): void {
    el('visiting').dataset.show = who ? 'true' : 'false';
    if (who) el('visiting-text').textContent = who;
  }

  toast(title: string, body: string, ms = 2600): void {
    const node = el('toast');
    el('toast-title').textContent = title;
    el('toast-body').textContent = body;
    node.dataset.show = 'true';
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      node.dataset.show = 'false';
    }, ms);
  }

  /* -------------------------------------------------------------- *
   * Countdown
   * -------------------------------------------------------------- */

  /** Pass null to hide the line entirely — an empty countdown is worse noise
   *  than no countdown. */
  setNextReady(text: string | null): void {
    const node = el('next-ready');
    node.dataset.show = String(text !== null);
    node.textContent = text ?? '';
  }

  /* -------------------------------------------------------------- *
   * Welcome back
   * -------------------------------------------------------------- */

  private awayClosed: (() => void) | null = null;

  /**
   * `onClose` exists because the reward for time away is a fact card, and a
   * fact card stacked on top of the summary that announced it is two scrims
   * deep. The cards wait for this sheet to be dismissed.
   */
  showAway(headline: string, body: string, onClose?: () => void): void {
    el('away-headline').textContent = headline;
    el('away-body').textContent = body;
    this.awayClosed = onClose ?? null;
    setOpen('away-modal', true);
  }

  private closeAway(): void {
    setOpen('away-modal', false);
    const cb = this.awayClosed;
    this.awayClosed = null;
    if (cb) window.setTimeout(cb, 260);
  }

  /* -------------------------------------------------------------- *
   * Register
   * -------------------------------------------------------------- */

  showRegister(view: RegisterView): void {
    const active = view.ranked.find((s) => s.id === view.activeId) ?? null;

    // The "you" card doubles as the prompt to sign — an unsigned gardener
    // still needs to see what their garden is worth before deciding.
    el('you-name').textContent = active ? active.name : 'Not signed yet';
    el('you-meta').textContent = active
      ? [
          active.from,
          `signed ${formatStampDate(active.stampedAt)}`,
          view.streak > 1 ? `${view.streak}-day streak` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : 'Sign the register to have your garden recorded here.';

    el('you-stats').innerHTML = active
      ? [
          stat(active.score, 'points'),
          stat(active.trees, active.trees === 1 ? 'tree' : 'trees'),
          stat(`${active.species}/${this.species.length}`, 'species'),
          stat(active.badges.length, 'badges'),
        ].join('')
      : '';

    const list = el('register-list');
    list.innerHTML = '';
    view.ranked.forEach((stamp, i) => {
      const row = document.createElement('li');
      row.className = 'register-row';
      row.dataset.you = String(stamp.id === view.activeId);
      row.innerHTML = `
        <span class="register-rank">${i + 1}</span>
        <span class="register-who">
          <span class="register-name">${escapeHtml(stamp.name)}</span>
          <span class="register-from">${escapeHtml(
            [stamp.from, formatStampDate(stamp.stampedAt)].filter(Boolean).join(' · '),
          )}</span>
        </span>
        <span class="register-score">
          <span class="register-points">${stamp.score}</span>
          <span class="register-trees">${stamp.trees} ${
            stamp.trees === 1 ? 'tree' : 'trees'
          }</span>
        </span>`;
      list.appendChild(row);
    });

    if (view.ranked.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'register-empty';
      empty.textContent = 'Nobody has signed this register yet.';
      list.appendChild(empty);
    }

    this.handover = view.ranked.length > 0;
    el('btn-sign').textContent = active
      ? 'Hand over — sign as someone new'
      : 'Sign the register';

    setOpen('register-modal', true);
  }

  private closeRegister(): void {
    setOpen('register-modal', false);
  }

  /* -------------------------------------------------------------- *
   * Signing
   * -------------------------------------------------------------- */

  private handover = false;

  showSign(): void {
    setOpen('register-modal', false);
    el<HTMLInputElement>('sign-name').value = '';
    el<HTMLInputElement>('sign-from').value = '';

    // Say plainly how the score will work, because a handover that silently
    // credited the newcomer with someone else's grove would read as broken.
    el('sign-title').textContent = this.handover
      ? 'Take over the garden'
      : 'Leave your mark on this garden';
    el('sign-body').textContent = this.handover
      ? 'You inherit the plot as it stands, but your score counts only what ' +
        'you grow from here. The previous gardener keeps theirs.'
      : 'Your entry is stamped with today’s date and stays on this device. ' +
        'Hand the phone on and the next gardener can sign underneath you.';

    setOpen('sign-modal', true);
    // Deliberately not autofocused: on a phone, a keyboard springing up over
    // the garden the moment a sheet opens reads as an ambush.
  }

  private closeSign(): void {
    setOpen('sign-modal', false);
  }

  private submitSign(): void {
    const name = el<HTMLInputElement>('sign-name').value.slice(0, MAX_NAME);
    const from = el<HTMLInputElement>('sign-from').value.slice(0, MAX_FROM);
    setOpen('sign-modal', false);
    this.cb.onSign(name, from);
  }

  /** A badge award. Distinct enough from a species toast to feel like a prize. */
  badgeToast(badge: Badge): void {
    this.prize(`${badge.glyph}  ${badge.name}`, badge.detail);
  }

  private prizes: { title: string; body: string }[] = [];
  private prizeBusy = false;

  /**
   * Queue a reward announcement: a badge, a challenge, a level.
   *
   * These arrive in bursts — one matured tree can complete a challenge, earn
   * a badge and cross a level in the same frame — and `toast` replaces
   * whatever is showing. Queued, they read as three prizes rather than one
   * toast overwriting the other two.
   */
  prize(title: string, body: string): void {
    this.prizes.push({ title, body });
    if (this.prizeBusy) return;
    // A short lead-in, so the first prize lands after the bloom and the
    // species note that usually caused it rather than on top of them.
    this.prizeBusy = true;
    window.setTimeout(() => this.nextPrize(), 900);
  }

  private nextPrize(): void {
    const next = this.prizes.shift();
    if (!next) {
      this.prizeBusy = false;
      return;
    }
    this.prizeBusy = true;
    this.toast(next.title, next.body, 3400);
    window.setTimeout(() => this.nextPrize(), 3700);
  }

  /* -------------------------------------------------------------- *
   * Level and journal
   * -------------------------------------------------------------- */

  private lastLevel = 0;

  /** The HUD ring. Pulses only when the level actually changes. */
  setLevel(level: number, progress: number): void {
    const ring = el('level-ring');
    ring.style.setProperty('--p', String(Math.round(Math.max(0, Math.min(1, progress)) * 100)));
    el('level-num').textContent = String(level);
    if (this.lastLevel !== 0 && level > this.lastLevel) {
      ring.classList.remove('bump');
      void ring.offsetWidth;
      ring.classList.add('bump');
      window.setTimeout(() => ring.classList.remove('bump'), 600);
    }
    this.lastLevel = level;
  }

  openJournal(view: JournalView): void {
    this.journal.open(view);
  }

  refreshJournal(view: JournalView): void {
    this.journal.refresh(view);
  }

  closeJournal(): void {
    this.journal.close();
  }

  get journalOpen(): boolean {
    return this.journal.isOpen;
  }

  mutationToast(mutation: Mutation, speciesName: string): void {
    this.toast(
      `${mutation.name} ${speciesName}`,
      `${mutation.detail} Worth ${mutation.bonus}× on the register.`,
      4200,
    );
  }

  /* -------------------------------------------------------------- *
   * Weather
   * -------------------------------------------------------------- */

  private lastWeather: string | null = null;

  /**
   * Update the sky chip. Pulses only on an actual change of weather, because
   * this is called every frame and an animation that retriggers constantly is
   * worse than none.
   */
  setWeather(weather: Weather): void {
    if (this.lastWeather === weather.id) return;
    const changed = this.lastWeather !== null;
    this.lastWeather = weather.id;

    const chip = el('weather-chip');
    chip.dataset.weather = weather.id;
    el('weather-glyph').textContent = weather.glyph;
    el('weather-name').textContent = weather.name;
    el('weather-note').textContent = weather.note;

    if (changed) {
      chip.classList.remove('turned');
      void chip.offsetWidth;
      chip.classList.add('turned');
    }
  }
}

/** `0x4fb3c4` → `#4fb3c4`, for handing a colour to CSS. */
export function hex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

function stat(value: string | number, label: string): string {
  return `<span class="stat"><span class="stat-num">${value}</span><span class="stat-label">${label}</span></span>`;
}

function setOpen(id: string, open: boolean): void {
  const node = el(id);
  node.dataset.open = String(open);
  node.setAttribute('aria-hidden', String(!open));
}

function isOpen(id: string): boolean {
  return document.getElementById(id)?.dataset.open === 'true';
}
